/**
 * SPECTREIQ API Core Client
 * 
 * WHY: This is the centralized HTTP client with global interceptors.
 * All module-specific API clients MUST use this base client to ensure
 * compliance headers (Mission-Context-ID, Client-Version) are attached
 * to every request per the Expert Review mandate.
 * 
 * @see .docs/architecture/software_iq_architecture_review.md - Section 3.2
 */

/** Application version injected at build time for audit traceability */
const CLIENT_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0-dev'

/** Base URL for the Platform Gateway - the ONLY external endpoint */
const API_BASE = import.meta.env.VITE_API_URL || '/api/v1'

/**
 * Global state for mission context.
 * WHY: The Compliance Officer mandated that Mission-Context-ID must be
 * included in every request for non-repudiation in audit logs.
 * This is set by the Zustand store and read by the interceptor.
 */
let globalMissionContextId: string | null = null
let globalAuthToken: string | null = null
let globalTenantCode: string | null = import.meta.env.VITE_TENANT_CODE || null

/**
 * Sets the global mission context for all subsequent requests.
 * Called by the Zustand store when mission context changes.
 */
export function setGlobalMissionContext(missionId: string | null): void {
  globalMissionContextId = missionId
}

/**
 * Sets the global auth token for all subsequent requests.
 * Called by the Keycloak provider on token refresh.
 */
export function setGlobalAuthToken(token: string | null): void {
  globalAuthToken = token
}

/**
 * Sets the global tenant code header for all subsequent requests.
 * Supports realm-per-tenant rollouts where API requests are tenant scoped.
 */
export function setGlobalTenantCode(tenantCode: string | null): void {
  globalTenantCode = tenantCode
}

/**
 * Structured API error with RFC 7807 Problem Details compatibility.
 * WHY: DoD applications require structured error responses for
 * automated error handling and audit logging.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly detail?: unknown,
    public readonly correlationId?: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** HTTP methods supported by the client */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'

/** Request configuration options */
export interface RequestConfig {
  method?: HttpMethod
  body?: unknown
  headers?: Record<string, string>
  /** Override the global auth token for this request */
  token?: string | null
  /** Override the global tenant code for this request */
  tenantCode?: string | null
  /** Override the global mission context for this request */
  missionContextId?: string | null
  /** Skip adding the mission context header (for non-mission endpoints) */
  skipMissionContext?: boolean
}

/**
 * Core request function with global interceptors.
 * 
 * WHY: This function implements the "Compliance Header" mandate from
 * the Expert Review. Every request includes:
 * - Authorization: Bearer token for Zero Trust
 * - Mission-Context-ID: For audit trail non-repudiation
 * - X-Client-Version: For traceability of which UI version generated data
 * - X-Correlation-ID: For distributed tracing
 * 
 * @template T - Expected response type
 * @param endpoint - API endpoint path (will be prefixed with API_BASE)
 * @param config - Request configuration
 * @returns Promise resolving to the typed response
 * @throws {ApiError} On non-2xx responses
 */
export async function request<T>(
  endpoint: string,
  config: RequestConfig = {}
): Promise<T> {
  const {
    method = 'GET',
    body,
    headers = {},
    token,
    tenantCode,
    missionContextId,
    skipMissionContext = false,
  } = config

  // Generate correlation ID for distributed tracing
  const correlationId = crypto.randomUUID()

  const isFormData =
    typeof FormData !== 'undefined' && body instanceof FormData

  // Build headers with compliance requirements
  const requestHeaders: Record<string, string> = {
    'X-Client-Version': CLIENT_VERSION,
    'X-Correlation-ID': correlationId,
    ...headers,
  }

  // Only set Content-Type for JSON payloads.
  // For FormData, the browser will set the correct multipart boundary.
  if (!isFormData) {
    requestHeaders['Content-Type'] = 'application/json'
  }

  // Authorization header (explicit token > global token)
  const effectiveToken = token !== undefined ? token : globalAuthToken
  if (effectiveToken) {
    requestHeaders['Authorization'] = `Bearer ${effectiveToken}`
  }

  const effectiveTenantCode = tenantCode !== undefined ? tenantCode : globalTenantCode
  if (effectiveTenantCode) {
    requestHeaders['X-Tenant-Code'] = effectiveTenantCode
  }

  // Mission context header (explicit > global, unless skipped)
  if (!skipMissionContext) {
    const effectiveMissionId = missionContextId !== undefined 
      ? missionContextId 
      : globalMissionContextId
    if (effectiveMissionId) {
      requestHeaders['Mission-Context-ID'] = effectiveMissionId
    }
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    method,
    headers: requestHeaders,
    body: body
      ? (isFormData ? (body as unknown as BodyInit) : JSON.stringify(body))
      : undefined,
  })

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}))
    throw new ApiError(
      errorBody.detail || `Request failed with status ${response.status}`,
      response.status,
      errorBody,
      correlationId
    )
  }

  // Handle empty responses (204 No Content)
  if (response.status === 204) {
    return {} as T
  }

  return response.json()
}

/**
 * Convenience methods for common HTTP operations.
 * These wrap the core request function with appropriate defaults.
 */
export const http = {
  get: <T>(endpoint: string, config?: Omit<RequestConfig, 'method' | 'body'>) =>
    request<T>(endpoint, { ...config, method: 'GET' }),

  post: <T>(endpoint: string, body: unknown, config?: Omit<RequestConfig, 'method' | 'body'>) =>
    request<T>(endpoint, { ...config, method: 'POST', body }),

  put: <T>(endpoint: string, body: unknown, config?: Omit<RequestConfig, 'method' | 'body'>) =>
    request<T>(endpoint, { ...config, method: 'PUT', body }),

  patch: <T>(endpoint: string, body: unknown, config?: Omit<RequestConfig, 'method' | 'body'>) =>
    request<T>(endpoint, { ...config, method: 'PATCH', body }),

  delete: <T>(endpoint: string, config?: Omit<RequestConfig, 'method' | 'body'>) =>
    request<T>(endpoint, { ...config, method: 'DELETE' }),
}
